import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';
import { operatorHeaders, requireOperatorToken } from './pos/operatorGrant';

const API_URL = 'citas/';
const API_URL_CATEGORY = 'categories/';
const API_URL_PRECIO = 'precio-plan-servicio/';
const API_URL_SERVICIO_ESPACIO = 'espacio-servicios/';
//const API_PUBLIC_URL = 'servicios-public/';
const API_URL_SERVICIOS = 'servicios/';
const API_URL_CONFIG_PLAN = 'plan-duracion-config/';
const API_URL_CLIENTE = 'clientes/';
const API_URL_CUPONES = 'cupones/';
const API_URL_USADOS = 'cupones-usados/';



export interface Categoria {
    id: number;
    nombre: string;
}


export interface Servicio {
    tipo_servicio?: 'orden_servicio' | 'oficina_virtual';
    id: number;
    categoria: number;
    categoria_nombre: string;
    nombre: string;
    descripcion: string;
    imagen: string;
    estado: boolean;
    requiere_espacio: boolean;
    fecha_creacion: string;
}


export interface ProductoReservaDetalle {
    id: number;
    producto: number;
    nombre: string;
    cantidad: number;
    precio_unitario: string;
    monto_descuento?: string;
    subtotal: string;
}

export interface Cita {
    oficina_virtual?: import('./afiliadosService').Afiliacion | null;
    productos_reserva?: ProductoReservaDetalle[];
    total_productos?: string;
    productos?: { producto: number; cantidad: number; monto_descuento?: string }[];
    id: number;
    cliente?: number | null;
    precio_plan_servicio: number;
    reglas_plan_reserva?: Record<string, any>;
    espacio_servicio: number | null;
    categoria: number | null;
    fecha_inicio?: string | null;
    fecha_fin?: string | null;
    hora_inicio?: string | null;
    hora_fin?: string | null;
    cantidad_periodos?: number | null;
    subtotal?: number | null;
    descuento_directo?: number | null;
    descuento_servicio?: number;
    total: number;
    estado: string;
    estado_ejecucion: 'PorComenzar' | 'EnCurso' | 'Finalizada' | 'Cancelada';
    codigo: string;
    dias_habiles: boolean;
    monto_pagado: number;
    estado_pago: string;
    sesion_pos?: number | null;
    es_asociado?: boolean;
    clientes_asociados?: number[];
    clientes_asociados_info?: Array<{
        id: number;
        first_name: string;
        last_name: string;
        email: string;
    }>;
    cliente_info?: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        telefono?: string | null;
        ci_nit?: string | null;
        foto_url?: string | null;
        tipo_nivel_usuario?: {
            id: number;
            nombre: string;
            perfil_asociado?: string;
        } | null;
        declaracion_jurada_firmada?: boolean;
        fecha_declaracion_jurada?: string | null;
        foto_declaracion_jurada_url?: string | null;
    } | null;
    sucursal?: number | null;
    sucursal_info?: {
        id: number;
        nombre: string;
    } | null;
    servicio_info?: {
        id: number;
        nombre: string;
    } | null;
    espacio_info?: {
        id: number;
        nombre: string;
    } | null;
    plan_info?: {
        id: number;
        plan_nombre: string;
        precio_inicial: string;
    } | null;
    cupon?: number | null;
    observaciones_ingreso?: string | null;
    observaciones_salida?: string | null;
    limpieza_ingreso?: boolean;
    limpieza_salida?: boolean;
}


export interface ReservationReport {
    reserva: {
        id: number;
        codigo: string;
        estado: string;
        estado_ejecucion?: string;
        fecha_inicio?: string | null;
        fecha_fin?: string | null;
        hora_inicio?: string | null;
        hora_fin?: string | null;
        servicio?: string | null;
        plan?: string | null;
        espacio?: string | null;
        sucursal?: string | null;
    };
    finanzas: {
        subtotal: string;
        descuento: string;
        total: string;
        pagado: string;
        deuda: string;
        estado_pago: string;
        pagos: Array<{ id: number; codigo?: string | null; monto: string; tipo_pago: string; fecha_pago: string; origen: string }>;
    };
    horas_regalo: {
        asignadas: number;
        usadas: number;
        disponibles_minutos: number;
        periodos: Array<{ id: number; fecha_inicio: string; fecha_fin: string; asignadas: number; usadas: number; disponibles_minutos: number }>;
    };
    correspondencia: {
        habilitada: boolean;
        total: number;
        items: Array<{ id: number; estado: string; descripcion?: string | null; fecha_recepcion: string; fecha_entrega?: string | null; remitente?: string }>;
    };
}

export interface TodayArrivalsReportData {
    fecha: string;
    advertencia?: string;
    total_reservas: number;
    total_clientes: number;
    reservas: Array<{
        fecha_ocurrencia?: string;
        id: number;
        codigo: string;
        hora_inicio?: string | null;
        hora_fin?: string | null;
        servicio?: string | null;
        espacio?: string | null;
        sucursal?: string | null;
        estado: string;
        estado_pago: string;
        total: string;
        pagado: string;
        deuda: string;
        clientes: Array<{ id: number; nombre: string; email: string; tipo: 'titular' | 'asociado' }>;
    }>;
}

export interface FutureArrivalsReportData {
    fecha_desde: string;
    fecha_hasta: string;
    total_reservas: number;
    total_ocurrencias: number;
    total_clientes: number;
    rango_limitado: boolean;
    dias: TodayArrivalsReportData[];
}

export const fetchReservaReport = async (id: number): Promise<ReservationReport> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<ReservationReport>(`${API_URL}${id}/reporte-reserva/`);
        return response.data;
    });
};

export const fetchTodayArrivalsReport = async (fecha: string, sucursalId?: number): Promise<TodayArrivalsReportData> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<TodayArrivalsReportData>(`${API_URL}llegadas-hoy/`, {
            params: { fecha, ...(sucursalId ? { sucursal_id: sucursalId } : {}) },
        });
        return response.data;
    });
};

export const fetchFutureArrivalsReport = async (
    fechaDesde: string,
    sucursalId?: number,
    fechaHasta?: string,
): Promise<FutureArrivalsReportData> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<FutureArrivalsReportData>(`${API_URL}llegadas-hoy/`, {
            params: {
                fecha_desde: fechaDesde,
                ...(fechaHasta ? { fecha_hasta: fechaHasta } : {}),
                ...(sucursalId ? { sucursal_id: sucursalId } : {}),
            },
        });
        return response.data;
    });
};

export const fetchCitas = async (sucursalId?: number): Promise<Cita[]> => {
    return handleRequestWithAuth(async () => {
        const params: any = {};
        if (sucursalId) {
            params.sucursal_id = sucursalId;
        }
        const response = await axiosInstance.get<Cita[]>(API_URL, { params });
        return response.data;
    });
};

export const createCita = async (data: {
    productos?: { producto: number; cantidad: number; monto_descuento?: string }[];
    precio_final?: string;
    motivo_precio?: string;
    precio_plan_servicio: number;
    cliente?: number | null;
    espacio_servicio: number | null;
    categoria: number;
    fecha_inicio?: string | null;
    fecha_fin?: string | null;
    hora_inicio?: string | null;
    hora_fin?: string | null;
    cantidad_periodos?: number | null;
    /** Ignorado por el backend: subtotal/total se derivan server-side. */
    subtotal?: number | null;
    /** Descuento manual acotado por el backend a [0, subtotal]; 0 con temporizador. */
    descuento_directo?: number | null;
    descuento_servicio?: number;
    /** Ignorado por el backend: subtotal/total se derivan server-side. */
    total?: number | null;
    dias_habiles: boolean;
    cupon?: number | null;
    /** Metadata no autoritativa: el backend nunca deriva el estado de aquí. */
    source?: string;
    clientes_asociados?: number[];
    observaciones_ingreso?: string | null;
    observaciones_salida?: string | null;
    limpieza_ingreso?: boolean;
    limpieza_salida?: boolean;
    usar_horas_afiliado?: boolean;
    usar_horas_domiciliado?: boolean;
    /** Sucursal declarada por el operador; el backend exige que coincida con la caja. */
    sucursal_id: number | null;
    /** Sesión POS explícita del operador; el backend no infiere ninguna. */
    sesion_pos: number | null;
}, operadorToken: string | null | undefined) => {
    // Crear una cita desde caja exige el grant del operador (PIN) sobre la
    // sesión POS explícita; el backend responde 403 sin él, así que sin grant
    // no se llama.
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(API_URL, data, config);
        return response.data;
    });
};

export const updateCita = async (
    id: number,
    data: {
        cliente?: number | null;
        precio_plan_servicio?: number | null;
        espacio_servicio?: number | null;
        categoria?: number | null;
        fecha_inicio?: string | null;
        fecha_fin?: string | null;
        hora_inicio?: string | null;
        hora_fin?: string | null;
        cantidad_periodos?: number | null;
        subtotal?: number | null;
        descuento_directo?: number | null;
        descuento_servicio?: number;
        total?: number;
        dias_habiles?: boolean;
        cupon?: number | null;
        clientes_asociados?: number[];
        es_extension?: boolean; // Nuevo campo para indicar extensión
        observaciones_ingreso?: string | null;
        observaciones_salida?: string | null;
        limpieza_ingreso?: boolean;
        limpieza_salida?: boolean;
    },
    operadorToken: string | null | undefined,
) => {
    // Mutar una cita registrada en caja exige el grant del operador sobre su
    // caja persistida (``X-POS-Operator-Token``); sin grant no se llama.
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    return handleRequestWithAuth(async () => {
        if (data.precio_plan_servicio === null) {
            throw new Error('precio_plan_servicio es requerido');
        }

        const response = await axiosInstance.put(`${API_URL}${id}/`, data, config);
        return response.data;
    });
};

export const patchCita = async (
    id: number,
    data: Partial<Cita>, operadorToken: string | null | undefined
) => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.patch(`${API_URL}${id}/`, data, operatorHeaders(requireOperatorToken(operadorToken)));
        return response.data;
    });
};

export const deleteCita = async (id: number, operadorToken: string | null | undefined) => {
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.delete(`${API_URL}${id}/`, config);
        return response.data;
    });
};

export const fetchCitasPorSucursal = async (sucursalId: number): Promise<Cita[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Cita[]>(API_URL, {
            params: { sucursal_id: sucursalId }
        });
        return response.data;
    });
};

export const fetchCitasByClienteId = (clienteId: number): Promise<Cita[]> =>
    fetchCitasPorUsuarioIncluyendoAsociadas(clienteId);

export interface PaginaCitas {
    count: number;
    next: string | null;
    previous: string | null;
    results: Cita[];
}

export const fetchMisReservasPagina = (page = 1): Promise<PaginaCitas> =>
    handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<PaginaCitas>(`${API_URL}mis-reservas/`, { params: { page } });
        return response.data;
    });

export const fetchMisCitas = async (): Promise<Cita[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Cita[]>(API_URL);
        return response.data;
    });
};

export interface FinalizarTemporizadorResponse {
    status: string;
    hora_fin: string;
    fecha_fin: string;
    horas_a_cobrar: number;
    costo_bruto: string;
    descuento: string;
    /** Total canónico derivado por el servidor (string decimal). */
    total: string;
}

/**
 * Finaliza el temporizador de una cita desde la sesión POS EXPLÍCITA y con el
 * grant del operador identificado por PIN. El backend exige una caja
 * compartida abierta, habilitada para citas y de la misma sucursal que la
 * cita; nunca infiere la caja ni el operador, por eso sin sesión o sin grant
 * no se llama al servidor.
 */
export const finalizarTemporizador = async (
    citaId: number,
    sesionPosId: number | null | undefined,
    operadorToken: string | null | undefined,
): Promise<FinalizarTemporizadorResponse> => {
    if (sesionPosId == null) {
        throw new Error('Necesitas una sesión POS abierta para finalizar el temporizador.');
    }
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(
            `${API_URL}${citaId}/finalizar-temporizador/`,
            { sesion_pos: sesionPosId },
            config,
        );
        return response.data;
    });
};

export const fetchCitasByClienteIda = async (userId: number): Promise<Cita[]> => {
    const todasLasCitas = await fetchCitas();
    return todasLasCitas.filter((cita) =>
        cita.cliente !== null && Number(cita.cliente) === Number(userId)
    );
};
// En citasService.ts
export const fetchCitasPorUsuarioId = async (userId: number): Promise<Cita[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Cita[]>(
            `${API_URL}citas_por_usuario/${userId}/`
        );
        return response.data;
    });
};

export const fetchCitaById = async (id: number): Promise<Cita> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Cita>(`${API_URL}${id}/`);
        return response.data;
    });
};

/**
 * Agrega acompañantes a una cita registrada en caja. Es una mutación de la
 * cita: el backend exige el grant del operador (PIN) sobre la caja PERSISTIDA
 * de la cita y responde 403 sin él, así que sin grant no se llama.
 */
export const agregarClientesAsociados = async (
    citaId: number,
    clientesIds: number[],
    operadorToken: string | null | undefined,
) => {
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(
            `${API_URL}${citaId}/agregar-clientes/`,
            { clientes: clientesIds },
            config,
        );
        return response.data;
    });
};

/** Quita un acompañante; misma autoridad que ``agregarClientesAsociados``. */
export const quitarClienteAsociado = async (
    citaId: number,
    clienteId: number,
    operadorToken: string | null | undefined,
) => {
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(
            `${API_URL}${citaId}/quitar-cliente/`,
            { cliente_id: clienteId },
            config,
        );
        return response.data;
    });
};

export const listarClientesAsociados = async (citaId: number) => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(
            `${API_URL}${citaId}/list-clientes/`
        );
        return response.data;
    });
};

// Agrega esta función al archivo citasService.ts

export const fetchCitasPorUsuarioCompleto = (userId: number): Promise<Cita[]> =>
    fetchCitasPorUsuarioIncluyendoAsociadas(userId);

// O crea un endpoint en el backend específico para esto (RECOMENDADO)
export const fetchCitasPorUsuarioIncluyendoAsociadas = async (userId: number): Promise<Cita[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Cita[]>(
            `${API_URL}?cliente_id=${userId}&incluir_asociadas=true`
        );
        return response.data.filter(cita => cita.cliente === userId ||
            cita.clientes_asociados?.includes(userId) || cita.es_asociado === true);
    });
};



export interface Notificacion {
    id: number;
    mensaje: string;
    leida: boolean;
    fecha_creacion: string;
    cita: number;
}

export const fetchNotificaciones = async (): Promise<Notificacion[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Notificacion[]>('notificaciones/');
        return response.data;
    });
};

/**
 * Confirma la cita de una notificación. Muta la cita, así que exige el grant
 * del operador sobre la caja persistida de la cita (403 sin él).
 */
export const confirmarCitaDesdeNotificacion = async (
    notificacionId: number,
    operadorToken: string | null | undefined,
): Promise<{ cita_id: number }> => {
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(`notificaciones/${notificacionId}/confirmar/`, {}, config);
        return response.data;
    });
};






export interface PrecioPlanServicio {
    id: number;
    servicio: number;
    servicio_nombre: string;
    plan_duracion_config: number;
    plan_nombre: string;
    precio_inicial: number;
    precio_posterior: number | null;
    duracion_inicial: number | null;
    fecha_creacion: string;
    estado: boolean;
    requiere_espacio: boolean;
}

export const fetchPrecioPlanesServicios = async (): Promise<PrecioPlanServicio[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<PrecioPlanServicio[]>(API_URL_PRECIO);
        return response.data;
    });
};

export interface EspacioServicio {
    id: number;
    espacio: number;
    espacio_nombre: string;
    espacio_imagen: string;
    servicio: number;
    fecha_creacion: string;
    estado: boolean;
    cantidad_personas: number;
}
export const fetchEspacioServicios = async (servicioId?: number, sucursalId?: number): Promise<EspacioServicio[]> => {
    return handleRequestWithAuth(async () => {
        const params: any = {};
        if (servicioId) {
            params.servicio = servicioId;
        }
        if (sucursalId) {
            params.sucursal_id = sucursalId;
        }
        const response = await axiosInstance.get<EspacioServicio[]>(API_URL_SERVICIO_ESPACIO, { params });
        return response.data;
    });
};




export const fetchCategories = async (sucursalId?: number) => {
    return handleRequestWithAuth(async () => {
        const params: any = {};
        if (sucursalId) {
            params.sucursal_id = sucursalId;
        }
        const response = await axiosInstance.get(API_URL_CATEGORY, { params });
        return response.data;
    });
};


export const fetchServiciosByCategory = async (categoryId: number, sucursalId?: number): Promise<Servicio[]> => {
    return handleRequestWithAuth(async () => {
        const params: any = { categoria: categoryId };
        if (sucursalId) {
            params.sucursal_id = sucursalId;
        }
        const response = await axiosInstance.get<Servicio[]>(API_URL_SERVICIOS, { params });
        return response.data;
    });
};


// En citasService.ts, dentro de la interfaz PlanDuracionConfig
export interface PlanDuracionConfig {
    reglas_reserva?: Record<string,any>;
    id: number;
    plan: number;
    plan_nombre: string;
    unidad: 'hora' | 'dia';
    dias: number | null;
    horas: number | null;
    usa_temporizador: boolean;   // ← agregar esta línea
    descripcion?: string;
    estado: boolean;
    fecha_creacion: string;
}

export const fetchPlanDuracionConfigs = async (): Promise<PlanDuracionConfig[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(API_URL_CONFIG_PLAN);
        return response.data;
    });
};





export const registerUser = async (
    email: string,
    firstName: string,
    lastName: string,
    password: string,
    estado: boolean,
    tipo_nivel_usuario: number | null
) => {
    try {
        const response = await axiosInstance.post('register/', {
            email,
            first_name: firstName,
            last_name: lastName,
            password,
            estado,
            tipo_nivel_usuario,
        });
        console.log('Usuario registrado correctamente:', response.data);
        return response.data;
    } catch (error: any) {
        console.error('Error al registrar usuario:', error.response?.data);
        const errorMessage =
            error.response?.data?.email?.[0] ||
            error.response?.data?.detail ||
            'Error de registro';
        throw new Error(errorMessage);
    }
};





export interface Cliente {
    id: number;
    usuario: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        tipo_nivel_usuario?: {
            id: number;
            nombre: string;
            perfil_asociado?: string;
        } | null;
    } | null;
    telefono: string | null;
    ci_nit: string | null;
    codigo_tipo_documento_identidad_sin?: number;
    complemento?: string;
    fecha_creacion?: string;
    foto_url?: string | null;
    declaracion_jurada_firmada?: boolean;
    foto_declaracion_jurada_url?: string | null;
    fecha_declaracion_jurada?: string | null;
}

export const fetchClientes = async (): Promise<Cliente[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Cliente[]>(API_URL_CLIENTE);
        return response.data;
    });
};

export const createCliente = async (data: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    telefono?: string | null;
    ci_nit?: string | null;
    tipo_nivel_usuario: number;
}) => {

    const user = await registerUser(
        data.email,
        data.first_name,
        data.last_name,
        data.password,
        true,
        data.tipo_nivel_usuario
    );

    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post<Cliente>(API_URL_CLIENTE, {
            usuario: user.id,
            telefono: data.telefono || null,
            ci_nit: data.ci_nit || null,
        });
        return {
            ...response.data,
            usuario: {
                id: user.id,
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
            }
        };
    });
};


export const updateCliente = async (
    id: number,
    data: {
        telefono?: string | null;
        ci_nit?: string | null;
        foto?: File | null;
        foto_declaracion_jurada?: File | null;
        declaracion_jurada_firmada?: boolean;
    }
) => {
    return handleRequestWithAuth(async () => {
        const formData = new FormData();
        if (data.telefono !== undefined) formData.append('telefono', data.telefono ?? '');
        if (data.ci_nit !== undefined) formData.append('ci_nit', data.ci_nit ?? '');
        if (data.foto) formData.append('foto', data.foto);
        if (data.foto_declaracion_jurada) formData.append('foto_declaracion_jurada', data.foto_declaracion_jurada);
        if (data.declaracion_jurada_firmada !== undefined) formData.append('declaracion_jurada_firmada', String(data.declaracion_jurada_firmada));

        const response = await axiosInstance.put<Cliente>(`${API_URL_CLIENTE}${id}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    });
};

export const fetchClienteById = async (id: number): Promise<Cliente> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Cliente>(`${API_URL_CLIENTE}${id}/`);
        return response.data;
    });
};


export interface Cupon {
    id: number;
    codigo: string;
    descripcion: string;
    descuento_monto: number | null;
    descuento_porcentaje: number | null;
    fecha_inicio: string;
    fecha_fin: string;
    fecha_creacion: string;
    uso_unico_por_cliente: boolean;
    plan_aplicable_id: number | null;
    plan_aplicable?: {
        id: number;
        plan_nombre: string;
        servicio_nombre: string;
        servicio: number;
    } | null;
}


// Catálogo completo: SOLO personal/administradores (los clientes reciben []).
export const fetchCupones = async (): Promise<Cupon[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Cupon[]>(API_URL_CUPONES);
        return response.data;
    });
};

/** Respuesta mínima de la validación de un código exacto (flujo de cliente). */
export type CuponValidado = Pick<Cupon, 'id' | 'codigo' | 'descuento_monto' | 'descuento_porcentaje' | 'plan_aplicable'>;

/**
 * Valida un código EXACTO en el servidor (vigencia, plan aplicable y uso por
 * cliente se comprueban allí). Nunca descarga ni enumera el catálogo.
 */
export const validarCuponPorCodigo = async (
    codigo: string,
    precioPlanServicioId?: number | null,
): Promise<CuponValidado> => {
    const limpio = (codigo || '').trim();
    if (!limpio) throw new Error('Indica el código del cupón.');
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post<CuponValidado>(`${API_URL_CUPONES}validar/`, {
            codigo: limpio,
            precio_plan_servicio: precioPlanServicioId ?? null,
        });
        return response.data;
    });
};

/** Mensaje legible para el rechazo de un cupón (sin revelar otros códigos). */
export const mensajeErrorCupon = (err: any, fallback = 'No se pudo validar el cupón.'): string => {
    const status = err?.response?.status;
    const data = err?.response?.data;
    if (status === 404) return 'Cupón no válido.';
    if (status === 429) return 'Demasiados intentos. Espera un momento e inténtalo de nuevo.';
    if (data && typeof data === 'object') {
        const first = Object.values(data).flat().find(v => typeof v === 'string');
        if (first) return first as string;
    }
    return fallback;
};



export interface CuponUsado {
    id: number;
    cliente: number;
    cliente_nombre: string;
    cupon: number;
    cupon_codigo: string;
    cita: number | null;
    cita_id: number | null;
    fecha_uso: string;
    fecha_creacion: string;
}


export const fetchCuponesUsados = async (): Promise<CuponUsado[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<CuponUsado[]>(API_URL_USADOS);
        return response.data;
    });
};




export interface ClienteRetorno {
    cliente_id: number;
    cliente_nombre: string;
    cantidad_citas: number;
    frecuencia_promedio_dias: number;
    ultima_cita_inicio: string;
    ultima_cita_fin: string;
    prox_cita_estimada: string;
    prox_cita_min: string | null;
    prox_cita_max: string | null;
    probabilidad_retorno: boolean;
    prediccion_modelo: boolean;
    categoria_retorno?: string;
    categoria_frecuencia_predicha?: string;
    // Campos de Declaración Jurada (opcionales, si el backend los incluye)
    declaracion_jurada_firmada?: boolean;
    fecha_declaracion_jurada?: string | null;
    tipo_nivel_usuario?: string;
}


export const fetchClientesRetorno = async (): Promise<ClienteRetorno[]> => {
    const response = await axiosInstance.get('/estadisticas/clientes-retorno/');
    return response.data;
};


export interface ServicioEstadistica {
    nombre_servicio: string;
    cantidad_usos: number;
    ingresos_totales: number;
}

export const fetchServiciosMasUsados = async (fechaInicio?: string, fechaFin?: string): Promise<ServicioEstadistica[]> => {
    let url = '/estadisticas/servicios-mas-usados/';
    if (fechaInicio && fechaFin) {
        url += `?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`;
    }
    const response = await axiosInstance.get<ServicioEstadistica[]>(url);
    return response.data;
};



export interface EspacioServicioInfo {
    id: number;
    espacio_nombre: string;
    espacio_imagen: string;
    cantidad_personas: number;
    servicio_nombre: string;
}

export interface PrecioPlanServicioInfo {
    id: number;
    servicio_nombre: string;
    plan_nombre: string;
    precio_inicial: number;
    precio_posterior: number | null;
    duracion_inicial: number | null;
}

export interface CategoriaInfo {
    id: number;
    nombre: string;
}

export interface CitaDetallada extends Cita {
    espacio_servicio_info?: EspacioServicioInfo;
    precio_plan_servicio_info?: PrecioPlanServicioInfo;
    categoria_info?: CategoriaInfo;
}

export const evaluarPlanReserva = async (data: any, operadorToken: string | null | undefined) => {
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    return handleRequestWithAuth(async () => (await axiosInstance.post('citas/evaluar-plan/', data, config)).data);
};

export const cancelarReserva = async (id: number, motivo: string, operadorToken: string | null | undefined): Promise<Cita> => {
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    return handleRequestWithAuth(async () => (await axiosInstance.post(`${API_URL}${id}/cancelar/`, {motivo}, config)).data);
};

// El servidor valida titularidad y ausencia de pagos bajo bloqueo de la reserva.
export const cancelarReservaCliente = async (id: number, motivo: string): Promise<Cita> =>
    handleRequestWithAuth(async () => (await axiosInstance.post(`${API_URL}${id}/cancelar/`, { motivo })).data);



